import { Dispatch } from "@reduxjs/toolkit";
import { AUDITNAVIGATIONTRACES, AUDITRACEACTIONS, ERRORMESSAGES, GLOBALSETTINGS } from "../data/constants";
import { FileActions, FolderFileType } from "../data/Enums";
import { AuditTraceRequest } from "../types/AuditLog";
import { AuditNavigationTrace } from "../types/AuditNavigationTrace";
import apiClient from "../utils/apiClient";
import { setRecentViewedFile, setRecentViewedFolder } from "../redux/recentViewedSlice";
import { ActionMappings, DefaultTrace, GetTraceMessages, GLOBALMESSAGES } from "../constants/messages";
import { appInsights } from "../utils/AppInsights";

export const getNavigationTraces = async (auditTraceRequest?: AuditTraceRequest): Promise<AuditNavigationTrace[]> => {
  const response = await apiClient.post<AuditNavigationTrace[]>(`/portal/auditLog/navigation-traces`, auditTraceRequest);
  return response.data;
};

// Save audit trace in localStorage
export const logAuditTrace = (log: AuditNavigationTrace, dispatch?: Dispatch | null, isTraceActions?: boolean) => {
  if (log.userId) {
    let isFailed = false;
    if (log.action.includes(GLOBALMESSAGES.FAILED_ADDITION)) {
      isFailed = true;
      log.action = log.action.replace(GLOBALMESSAGES.FAILED_ADDITION, "").trim()
    }

    if (isTraceActions) {
      const isValidAction = Object.values(AUDITRACEACTIONS).includes(log.action);
      if (isValidAction) {
        const traceMessages = GetTraceMessages(log.objectType, log.objectName, isFailed);
        const normalizedActionKey = ActionMappings[log.action] as keyof typeof traceMessages;
        const friendlyMessage = traceMessages[normalizedActionKey] || DefaultTrace(log.action, log.objectName);
        log.objectName = friendlyMessage;
      }
    }
    else {
      const isValidAction = Object.values(FileActions).includes(log.action as FileActions);
      if (isValidAction) {
        const traceMessages = GetTraceMessages(log.objectType, log.objectName, isFailed);
        const normalizedActionKey = ActionMappings[log.action] as keyof typeof traceMessages;
        const friendlyMessage = traceMessages[normalizedActionKey] || DefaultTrace(log.action, log.objectName);
        log.objectName = friendlyMessage;
      }
    }

    const logs: AuditNavigationTrace[] = JSON.parse(localStorage.getItem(AUDITNAVIGATIONTRACES.AUDIT_STORAGE_KEY) || "[]");
    const logExists = logs.some(existingLog => {
      const timeDiff = Math.abs(new Date(existingLog.navigationDateTime).getTime() - new Date(log.navigationDateTime).getTime());
      return (
        existingLog.userId === log.userId &&
        existingLog.objectType === log.objectType &&
        existingLog.objectId === log.objectId &&
        existingLog.objectName === log.objectName &&
        existingLog.action === log.action &&
        timeDiff < GLOBALSETTINGS.MAX_TIME_DIFF_MS
      );
    });

    if (!logExists) {
      logs.push(log);
      localStorage.setItem(AUDITNAVIGATIONTRACES.AUDIT_STORAGE_KEY, JSON.stringify(logs));
      setRecentlyViewedItems(log, dispatch);
      (async () => {
        await sendLogsToServer();
      })();
    }
  }
};

export const removeRecentViewedItem = async (log: AuditNavigationTrace, dispatch?: Dispatch | null) => {
  if ((log.objectType === FolderFileType[FolderFileType.Folder] ||
    log.objectType === FolderFileType[FolderFileType.File]) && log.objectId
  ) {
    const storageName = log.objectType === FolderFileType[FolderFileType.Folder] ? AUDITNAVIGATIONTRACES.RECENT_VIEWED_FOLDERS
      : AUDITNAVIGATIONTRACES.RECENT_VIEWED_FILES;
    const recentlyViewedItems: AuditNavigationTrace[] = JSON.parse(localStorage.getItem(storageName) || "[]");
    const updatedItems = recentlyViewedItems.filter(item => item.objectId !== log.objectId);
    localStorage.setItem(storageName, JSON.stringify(updatedItems));
    if (dispatch) {
      dispatch(log.objectType === FolderFileType[FolderFileType.Folder] ? setRecentViewedFolder() : setRecentViewedFile());
    }
  }
}

const setRecentlyViewedItems = async (log: AuditNavigationTrace, dispatch?: Dispatch | null) => {
  if ((log.objectType === FolderFileType[FolderFileType.Folder] ||
    log.objectType === FolderFileType[FolderFileType.File]) && log.objectId
  ) {
    const storageName = log.objectType === FolderFileType[FolderFileType.Folder] ? AUDITNAVIGATIONTRACES.RECENT_VIEWED_FOLDERS
      : AUDITNAVIGATIONTRACES.RECENT_VIEWED_FILES;
    const recentlyViewedItems: AuditNavigationTrace[] = JSON.parse(localStorage.getItem(storageName) || "[]");

    const logExists = log.action !== FileActions.MoveToBin.toString() &&
      log.action !== FileActions.Publish.toString() &&
      recentlyViewedItems.some(existingLog =>
        existingLog.objectId === log.objectId &&
        existingLog.objectName === log.objectName
      );
    if (!logExists) {
      recentlyViewedItems.push(log);
      localStorage.setItem(storageName, JSON.stringify(recentlyViewedItems));
    }
    if (dispatch) {
      dispatch(log.objectType === FolderFileType[FolderFileType.Folder] ? setRecentViewedFolder() : setRecentViewedFile());
    }
  }
}

let isFlushing = false;

export const sendLogsToServer = async () => {
  if (isFlushing) return;  

  isFlushing = true;
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const logs: AuditNavigationTrace[] = JSON.parse(
        localStorage.getItem(AUDITNAVIGATIONTRACES.AUDIT_STORAGE_KEY) || "[]"
      );
      if (logs.length === 0) break;

      await apiClient.post(`/portal/auditLog/save-audit-traces`, logs);

      // Remove only what was just sent. A trace logged while that post was in flight (the Run
      // screen logs "Opened workflow case" and "Landed on stage" back to back) was appended to the
      // queue after `logs` was read; clearing the whole key dropped it unsent (Epic 80431, 2026-10-21).
      // logAuditTrace only ever appends, so the entries added meanwhile are the ones past `logs`.
      const queued: AuditNavigationTrace[] = JSON.parse(
        localStorage.getItem(AUDITNAVIGATIONTRACES.AUDIT_STORAGE_KEY) || "[]"
      );
      const unsent = queued.slice(logs.length);
      if (unsent.length) localStorage.setItem(AUDITNAVIGATIONTRACES.AUDIT_STORAGE_KEY, JSON.stringify(unsent));
      else localStorage.removeItem(AUDITNAVIGATIONTRACES.AUDIT_STORAGE_KEY);
    }
  } catch (apiError) {
    appInsights?.trackException({
      exception: new Error(ERRORMESSAGES.APIErrorLogging, apiError),
    });
     isFlushing = false;
  }

  isFlushing = false;
};
